export const ALUVO_WEB_HOSTS = ['aluvo.app', 'www.aluvo.app'];
export const ALUVO_WEB_ORIGIN = 'https://aluvo.app';
export const ALUVO_NATIVE_SCHEME = 'aluvo://';

export const APP_DEEP_LINK_DESTINATIONS = Object.freeze({
    MOVIE: 'movie',
    TV: 'tv',
    TV_SEASON: 'tv_season',
    PROFILE: 'profile',
    WATCHLIST: 'watchlist',
    COLLECTION: 'collection',
    CURATED_LIST: 'curated_list',
    SHARED_LIST: 'shared_list',
    // My Top Movies: /top-movies (creator) and /top-movies/:slug (shared list).
    TOP_MOVIES: 'top_movies',
    SHARED_TOP_MOVIES: 'shared_top_movies',
    // Custom Rankings: /rankings/new[?from=:slug] (editor; Make Your Own) and
    // /rankings/:slug (a shared ranking).
    NEW_RANKING: 'new_ranking',
    SHARED_RANKING: 'shared_ranking',
    COIN: 'coin',
    ACHIEVEMENT: 'achievement',
    QUIZ: 'quiz',
    // Canonical quiz deep link: /verify/movie/:tmdbId or /verify/tv/:tmdbId/season/:n.
    // Every lifecycle email / push uses this; clients resolve their own gates
    // (auth, DOB, email verification) before starting the quiz.
    VERIFY: 'verify',
    // Email verification / email-change confirmation: /verify-email?token=…
    // (session-bound; the app finishes it for the signed-in account).
    VERIFY_EMAIL: 'verify_email',
});

function cleanSegment(value) {
    return encodeURIComponent(String(value ?? '').trim());
}

function getUrl(input) {
    try {
        if (input instanceof URL) return input;
        return new URL(input, ALUVO_WEB_ORIGIN);
    } catch {
        return null;
    }
}

function withoutTrailingSlash(path) {
    const cleaned = path.replace(/\/+$/, '');
    return cleaned || '/';
}

function getRoutePath(url) {
    if (url.protocol === 'aluvo:') {
        const hostPath = url.hostname ? `/${url.hostname}` : '';
        return withoutTrailingSlash(`${hostPath}${url.pathname}`);
    }
    return withoutTrailingSlash(url.pathname);
}

function destination(type, webPath, nativePath = webPath, params = {}) {
    return { type, webPath, nativePath, params };
}

export function buildCanonicalWebPath(type, params) {
    switch (type) {
        case APP_DEEP_LINK_DESTINATIONS.MOVIE:
            return `/movie/movie/${cleanSegment(params.tmdbId)}`;
        case APP_DEEP_LINK_DESTINATIONS.TV:
            return `/movie/tv/${cleanSegment(params.tmdbId)}`;
        case APP_DEEP_LINK_DESTINATIONS.TV_SEASON:
            return `/movie/tv/${cleanSegment(params.tmdbId)}/season/${cleanSegment(params.seasonNumber)}`;
        case APP_DEEP_LINK_DESTINATIONS.PROFILE:
            return `/u/${cleanSegment(params.username)}`;
        case APP_DEEP_LINK_DESTINATIONS.WATCHLIST:
            return `/u/${cleanSegment(params.username)}/watchlist`;
        case APP_DEEP_LINK_DESTINATIONS.COLLECTION:
            return `/collection/${cleanSegment(params.collectionId ?? params.slug)}`;
        case APP_DEEP_LINK_DESTINATIONS.CURATED_LIST:
            return `/discover/lists/${cleanSegment(params.slug)}`;
        case APP_DEEP_LINK_DESTINATIONS.SHARED_LIST:
            return `/l/${cleanSegment(params.slug)}`;
        case APP_DEEP_LINK_DESTINATIONS.TOP_MOVIES:
            return '/top-movies';
        case APP_DEEP_LINK_DESTINATIONS.SHARED_TOP_MOVIES:
            return `/top-movies/${cleanSegment(params.slug)}`;
        case APP_DEEP_LINK_DESTINATIONS.NEW_RANKING:
            return params.from ? `/rankings/new?from=${cleanSegment(params.from)}` : '/rankings/new';
        case APP_DEEP_LINK_DESTINATIONS.SHARED_RANKING:
            return `/rankings/${cleanSegment(params.slug)}`;
        case APP_DEEP_LINK_DESTINATIONS.COIN:
            return `/coin/${cleanSegment(params.coinId)}`;
        case APP_DEEP_LINK_DESTINATIONS.ACHIEVEMENT:
            return `/achievement/${cleanSegment(params.achievementId)}`;
        case APP_DEEP_LINK_DESTINATIONS.QUIZ:
            return '/quiz';
        case APP_DEEP_LINK_DESTINATIONS.VERIFY:
            return buildVerifyPath(params.kind, params.tmdbId, params.seasonNumber);
        default:
            return null;
    }
}

/**
 * Canonical quiz deep link path. Mirrors apps/backend/src/utils/verifyLinks.js.
 */
export function buildVerifyPath(kind, tmdbId, seasonNumber = null) {
    const id = cleanSegment(tmdbId);
    if (!id) return null;
    if (kind === 'tv') {
        if (seasonNumber === null || seasonNumber === undefined || seasonNumber === '') return null;
        return `/verify/tv/${id}/season/${cleanSegment(seasonNumber)}`;
    }
    if (kind === 'movie') return `/verify/movie/${id}`;
    return null;
}

export function buildCanonicalWebUrl(type, params, origin = ALUVO_WEB_ORIGIN) {
    const path = buildCanonicalWebPath(type, params);
    return path ? `${origin.replace(/\/+$/, '')}${path}` : null;
}

export function buildNativeDeepLink(type, params) {
    const path = buildCanonicalWebPath(type, params);
    return path ? `${ALUVO_NATIVE_SCHEME}${path.replace(/^\/+/, '')}` : null;
}

export function resolveAppDestinationFromWebUrl(input) {
    const url = getUrl(input);
    if (!url) return null;
    if (url.protocol.startsWith('http') && !ALUVO_WEB_HOSTS.includes(url.hostname)) return null;

    const path = getRoutePath(url);
    const segments = path.split('/').filter(Boolean).map(decodeURIComponent);

    if (segments[0] === 'movie' && (segments[1] === 'movie' || segments[1] === 'tv') && segments[2]) {
        const kind = segments[1];
        if (kind === 'tv' && segments[3] === 'season' && segments[4]) {
            return destination(APP_DEEP_LINK_DESTINATIONS.TV_SEASON, path, `/movie/tv/${segments[2]}/season/${segments[4]}`, {
                kind,
                tmdbId: segments[2],
                seasonNumber: segments[4],
            });
        }
        return destination(kind === 'movie' ? APP_DEEP_LINK_DESTINATIONS.MOVIE : APP_DEEP_LINK_DESTINATIONS.TV, path, `/movie/${kind}/${segments[2]}`, {
            kind,
            tmdbId: segments[2],
        });
    }

    if ((segments[0] === 'u' || segments[0]?.startsWith('@')) && (segments[1] || segments[0])) {
        const username = segments[0] === 'u' ? segments[1] : segments[0].slice(1);
        if (!username) return null;
        if (segments[2] === 'watchlist') {
            return destination(APP_DEEP_LINK_DESTINATIONS.WATCHLIST, path, `/u/${username}/watchlist`, { username });
        }
        return destination(APP_DEEP_LINK_DESTINATIONS.PROFILE, path, `/u/${username}`, { username });
    }

    if (segments[0] === 'user' && segments[1]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.PROFILE, path, `/u/${segments[1]}`, { username: segments[1] });
    }

    if (segments[0] === 'coin' && segments[1]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.COIN, path, path, { coinId: segments[1] });
    }

    if (segments[0] === 'achievement' && segments[1]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.ACHIEVEMENT, path, path, { achievementId: segments[1] });
    }

    if (segments[0] === 'collection' && segments[1]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.COLLECTION, path, path, { collectionId: segments[1] });
    }

    if (segments[0] === 'collections' && segments[1]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.COLLECTION, path, `/collection/${segments[1]}`, { collectionId: segments[1], slug: segments[1] });
    }

    if (segments[0] === 'app' && segments[1] === 'collections' && segments[2]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.COLLECTION, path, `/collection/${segments[2]}`, { collectionId: segments[2], slug: segments[2] });
    }

    if (segments[0] === 'discover' && segments[1] === 'lists' && segments[2]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.CURATED_LIST, path, path, { slug: segments[2] });
    }

    if (segments[0] === 'l' && segments[1]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.SHARED_LIST, path, path, { slug: segments[1] });
    }

    if (segments[0] === 'top-movies') {
        if (segments[1]) return destination(APP_DEEP_LINK_DESTINATIONS.SHARED_TOP_MOVIES, path, path, { slug: segments[1] });
        return destination(APP_DEEP_LINK_DESTINATIONS.TOP_MOVIES, path);
    }

    if (segments[0] === 'rankings' && segments[1]) {
        if (segments[1] === 'new') {
            const from = url.searchParams.get('from');
            return destination(APP_DEEP_LINK_DESTINATIONS.NEW_RANKING, path, path, from ? { from } : {});
        }
        if (!segments[2]) return destination(APP_DEEP_LINK_DESTINATIONS.SHARED_RANKING, path, path, { slug: segments[1] });
        return null;
    }

    if (segments[0] === 'quiz' && !segments[1]) {
        return destination(APP_DEEP_LINK_DESTINATIONS.QUIZ, path);
    }

    if (segments[0] === 'verify-email' && segments.length === 1) {
        const token = url.searchParams.get('token');
        // The path never carries the token, so anything that records the
        // destination path can't leak it.
        return token ? destination(APP_DEEP_LINK_DESTINATIONS.VERIFY_EMAIL, '/verify-email', '/verify-email', { token }) : null;
    }

    if (segments[0] === 'verify' && (segments[1] === 'movie' || segments[1] === 'tv') && segments[2]) {
        const kind = segments[1];
        if (kind === 'tv') {
            if (segments[3] !== 'season' || !segments[4]) return null;
            return destination(APP_DEEP_LINK_DESTINATIONS.VERIFY, path, path, {
                kind, tmdbId: segments[2], seasonNumber: segments[4], attempt: url.searchParams.get('attempt'),
            });
        }
        return destination(APP_DEEP_LINK_DESTINATIONS.VERIFY, path, path, {
            kind, tmdbId: segments[2], seasonNumber: null, attempt: url.searchParams.get('attempt'),
        });
    }

    return null;
}
